// material
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Snackbar,
  Alert,
  Container,
  Stack,
  Button,
  Typography,
  Card
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Header from '../components/navbar/Header';
import HelpCenterDrawer from '../components/_dashboard/smartlock/HelpCenterDrawer';
import { HeaderHeight, HeaderHeightMobile, lockIcon } from '../Constants';
import { GetDashboardStatsData } from '../network/Stats';
// components
import Page from '../components/Page';
import {
  SmartLockDevices,
  TripCounts,
  AddSmartLockDeviceModal,
  AddSmartLockNewTrip
} from '../components/_dashboard/smartlock';
import MapView from '../components/MapView';
import useFeatures from '../components/customContextProviders/FeatureContext';
import { useAuth } from '../components/customContextProviders/AuthContext';

const useStyles = makeStyles({
  hidden: {
    display: 'none !important'
  },
  show: {
    display: 'block !important'
  }
});

export default function SmartLockDashboard() {
  const classes = useStyles();
  const feature = useFeatures();
  const { sessionUserData } = useAuth();
  const userRole = sessionUserData?.user?.userRole?.roleName;

  const [open, setOpen] = useState(false);
  const [userGeoLocations, setUserGeoLocations] = useState([]);
  const navigate = useNavigate();
  const [dashboardStatsData, setDashboardStatsData] = useState({});

  const gpsLocationPoller = useRef({});
  const [isAssignedDeviceModal, setAssignedDeviceModal] = useState(false);
  const [isAddTripModalOpen, setIsAddTripModalOpen] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [deviceList, setDeviceList] = useState([]);
  const [mapKey, setMapKey] = useState(1);
  const [filteredData, setFilteredData] = useState(dashboardStatsData?.deviceData);
  const [draweropen, setDrawerOpen] = useState(false);
  const [loader, setLoader] = useState(false);

  const categories = [
    {
      category: 'On Duty',
      count:
        dashboardStatsData &&
        dashboardStatsData?.deviceData?.filter(
          (obj) => obj?.device_status?.toUpperCase() === 'ASSIGNED'
        )?.length,
      id: 1,
      color: '#027148'
    },
    {
      category: 'Awaiting Duty',
      count:
        dashboardStatsData &&
        dashboardStatsData?.deviceData?.filter(
          (obj) => obj?.device_status?.toUpperCase() === 'UNASSIGNED' && obj?.latestEvent !== null
        )?.length,
      id: 2,
      color: '#FF5733'
    },
    {
      category: 'Offline',
      count:
        dashboardStatsData &&
        dashboardStatsData?.deviceData?.filter(
          (obj) =>
            obj?.deviceLiveStatus?.toUpperCase() === 'OFFLINE' &&
            obj?.device_status?.toUpperCase() === 'ASSIGNED'
        )?.length,
      id: 3,
      color: '#8B0000'
    },
    {
      category: 'Error',
      count:
        dashboardStatsData &&
        dashboardStatsData?.deviceData?.filter((obj) => obj?.latestEvent === null)?.length === 0
          ? 0
          : dashboardStatsData?.deviceData?.filter((obj) => obj?.latestEvent === null)?.length,
      id: 4,
      color: '#FFC300'
    }
  ];

  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    vertical: 'bottom',
    horizontal: 'left',
    msg: '',
    variant: 'filled',
    severity: ''
  });

  const handleSnackBarClose = (event, reason) => {
    // if (reason === 'clickaway') {
    //   return;
    // }
    setAlertMetas({
      ...alertMetas,
      snackBarOpened: false
    });
  };

  useEffect(() => {
    callDashboardStatsData();
    return () => {
      setUserGeoLocations((userGeoLocations) => []);
      gpsLocationPoller.current = {};
    };
  }, []);

  const callDashboardStatsData = async () => {
    setLoader(true);
    try {
      const res = await GetDashboardStatsData();
      if (res) {
        setLoader(false);
        setDashboardStatsData(res?.data?.data);
      }
    } catch (error) {
      setLoader(false);
      console.log(error);
    }
  };

  const handleAddMsg = (d) => {
    callDashboardStatsData();
  };

  const getFilteredData = (data) => {
    if (selectedState === 'Online') {
      return data.filter((item) => item.deviceLiveStatus.toUpperCase() === 'ONLINE');
    }
    if (selectedState === 'Offline') {
      return data.filter((item) => item.deviceLiveStatus.toUpperCase() === 'OFFLINE');
    }
    return data;
  };

  const getSelectedData = (data) => {
    if (data !== undefined) {
      let newList = [];
      if (selectedState === 'Offline') {
        newList = data?.filter(
          (item) =>
            item?.deviceLiveStatus?.toUpperCase() === 'OFFLINE' &&
            item?.device_status === 'assigned'
        );
      } else if (selectedState === 'Awaiting Duty') {
        newList = data?.filter(
          (item) => item?.device_status === 'unassigned' && item?.latestEvent !== null
        );
      } else if (selectedState === 'On duty') {
        newList = data?.filter((item) => item?.device_status === 'assigned');
      } else if (selectedState === 'Error') {
        newList = [];
      } else {
        newList = [...data];
      }
      setDeviceList([...newList]);
    }
  };

  useEffect(() => {
    getSelectedData(dashboardStatsData?.deviceData);
  }, [selectedState, dashboardStatsData, setDeviceList]);

  useEffect(() => {
    setFilteredData(getFilteredData(dashboardStatsData?.deviceData));
  }, [selectedState, dashboardStatsData]);

  return (
    <Page title="Lockator">
      <Header
        onOpenSidebar={() => setOpen(true)}
        // handleSearch={handleTripSearch}
        // handleSubmit={searchTrip}
        helpdrawer={setDrawerOpen}
        setHelpDrawer={setDrawerOpen}
        name="Dashboard"
        showSearch={false}
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        open={alertMetas?.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert variant={alertMetas?.variant} severity={alertMetas?.severity}>
          {alertMetas?.msg}
        </Alert>
      </Snackbar>

      <Container
        maxWidth="false"
        sx={{
          padding: '0 !important',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: {
            xs: `calc(100% - ${HeaderHeightMobile}px)`,
            md: `calc(100% - ${HeaderHeight}px)`
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px'
          }}
        >
          {feature?.trip?.addTrip && (
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setIsAddTripModalOpen(true)}
              sx={{ backgroundColor: '#FFFFFF' }}
            >
              Add Trip
            </Button>
          )}
          {/* {userRole?.toUpperCase() === 'SUPERADMIN' && (
            <Button
              variant="outlined"
              size="medium"
              className={classes?.actionButtons}
              onClick={() => setAssignedDeviceModal(true)}
              sx={{ backgroundColor: '#FFFFFF' }}
            >
              Add Device
            </Button>
          )} */}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6}>
            <SmartLockDevices dashboardData={dashboardStatsData} loader={loader} />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TripCounts admindashboardData={dashboardStatsData} loader={loader} />
          </Grid>
        </Grid>

        <Card
          data-testid="live-fleet-density"
          sx={{
            mt: 'auto',
            border: '1px solid #B0B9C780',
            minHeight: { xs: '60vh', sm: '40vh' },
            flexGrow: 1,
            borderRadius: '20px'
          }}
        >
          <Grid container spacing={1} sx={{ height: '100%', margin: '0' }}>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                height: { xs: 'max-content', sm: '100%' },
                padding: '0 !important'
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: { xs: 'max-content', sm: '100%' }
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '20px',
                    color: '#171D40'
                  }}
                >
                  Live Fleet Density
                </Typography>
                <Box
                  sx={(theme) => ({
                    textAlign: 'center',
                    mt: 5,
                    [theme.breakpoints.down('sm')]: {
                      mt: 1
                    },
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column' },
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 0 }
                  })}
                >
                  {categories?.map((item, index) => (
                    <Stack
                      key={item?.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={(theme) => ({
                        mt: 1,
                        textAlign: 'center',
                        [theme.breakpoints.down('sm')]: {
                          marginTop: '4px'
                        }
                      })}
                    >
                      <img src={lockIcon(item?.color)} alt="lock" />
                      <Typography
                        sx={(theme) => ({
                          color: '#6F6F6F',
                          fontSize: '12px',
                          fontWeight: 700,
                          lineHeight: '15px',
                          [theme.breakpoints.down('sm')]: {
                            fontSize: '10px',
                            lineHeight: '12px'
                          }
                        })}
                      >
                        {item?.category} {item.count !== undefined && `(${item.count})`}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={9}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: '0 !important'
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%'
                }}
              >
                <MapView
                  deviceList={dashboardStatsData?.deviceData}
                  setAlertMetas={setAlertMetas}
                  alertMetas={alertMetas}
                />
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Container>
      {/* {isAssignedDeviceModal && (
        <AddSmartLockDeviceModal
          openAssignedModal={isAssignedDeviceModal}
          handleClose={() => setAssignedDeviceModal(false)}
          onAction={(d) => handleAddMsg(d)}
          alertMetas={alertMetas}
          setAlertMetas={setAlertMetas}
        />
      )} */}
      {isAddTripModalOpen && (
        <AddSmartLockNewTrip
          openAssignedModal={isAddTripModalOpen}
          handleClose={() => setIsAddTripModalOpen(false)}
          onAction={(d) => handleAddMsg(d)}
          alertMetas={alertMetas}
          setAlertMetas={setAlertMetas}
        />
      )}
      {draweropen && (
        <HelpCenterDrawer
          open={draweropen}
          setOpen={setDrawerOpen}
          alertMetas={alertMetas}
          setAlertMetas={setAlertMetas}
        />
      )}
    </Page>
  );
}
